import { SsuLogoPlaceholder, useFormatter, useIntl, useSettings } from '@capsicum/common';
import {
	type NCWConfirmTransferAmountCardProps,
	type NCWConfirmTransferFeeCardProps,
	type NCWConfirmTransferResult,
	ncwSendAssetAtom,
	ncwTransferAmountAtom,
	useConvertChilizToFiatAndFormat
} from '@capsicum/features/nc-wallet/shared';
import { useWalletWithdrawRecipient } from '@capsicum/features/wallet/shared';
import { useNCWBalance } from '@capsicum/socios-core';
import { useBrowserTransaction } from '@socios-blk/browser';
import { useNonCustodialWallet } from '@socios-blk/core';
import { useAtom } from 'jotai';
import { useNavigationToTransactionDetails, useRouter } from 'libs/common-web/hooks';
import { ModalService } from 'libs/common-web/services';
import { useEffect, useMemo } from 'react';
import { match } from 'ts-pattern';

import { NCWSendErrorDrawer } from '../../ui/drawers/ncw-send-error/NCWSendErrorDrawer';
import { NCWSendResumeDrawer } from '../../ui/drawers/ncw-send-resume/NCWSendResumeDrawer';

export type NCWConfirmTransferProps = {
	recipientId: string | undefined;
};

type UseNCWConfirmTransfer = (params: NCWConfirmTransferProps) => NCWConfirmTransferResult;

export const useNCWConfirmTransfer: UseNCWConfirmTransfer = ({ recipientId }) => {
	const { $t } = useIntl();
	const { replace } = useRouter();
	const { formatCrypto } = useFormatter();
	const { data: recipient } = useWalletWithdrawRecipient(recipientId);
	const { data: nCWallet } = useNonCustodialWallet();
	const ncwBalance = useNCWBalance();
	const convertChilizToFiatAndFormat = useConvertChilizToFiatAndFormat(ncwBalance);
	const { handleTransactionNavigation } = useNavigationToTransactionDetails({ redirectType: 'REPLACE' });
	const { estimateGasFee, gasFeeEstimate, isError, isPending, sendTransaction, TRANSACTIONS } =
		useBrowserTransaction();

	const {
		settings: { isTransactionHistoryEnabled }
	} = useSettings();

	const intlTexts = {
		alertMessage: $t('ncWallet.send.confirmation.alertMessage'),
		continueButtonLabel: $t('wallet.withdraw.confirmation.continueButtonLabel'),
		title: $t('ncWallet.send.confirmation.pageTitle')
	};

	const fromAddress = nCWallet?.nonCustodialWallet?.blockchainAddress;
	const toAddress = recipient?.blockchainAddress;

	const [amount, setAmount] = useAtom(ncwTransferAmountAtom);
	const [asset, setAsset] = useAtom(ncwSendAssetAtom);

	const assetType = asset?.assetType;

	const smartContractAddress = match(assetType)
		.with('chiliz', () => null)
		.with('token', () => asset?.smartContractAddress)
		.otherwise(() => null);

	/**
	 * Prepare the blockchain transaction to be executed with the withdraw transaction.
	 */
	const transaction = useMemo(() => {
		if (!amount || !toAddress) {
			return undefined;
		}

		if (assetType === 'chiliz') {
			return [
				TRANSACTIONS.WITHDRAW_CHZ({
					amount,
					to: toAddress
				})
			];
		}

		if (assetType === 'token' && smartContractAddress) {
			return [
				TRANSACTIONS.WITHDRAW_UTILITY_TOKEN({
					amount: amount,
					contractAddress: smartContractAddress,
					decimals: asset?.decimalDetails,
					destinationWalletAddress: toAddress
				})
			];
		}

		return undefined;
	}, [amount, toAddress, smartContractAddress]);

	/**
	 * Estimate the gas fee for the transaction.
	 */
	useEffect(() => {
		if (gasFeeEstimate || !transaction || isPending || isError) {
			return;
		}

		estimateGasFee({
			onError: () => {
				replace('/nc-wallet');
				openErrorDrawer();
			},
			transaction
		});
	}, [gasFeeEstimate, transaction]);

	/**
	 * Verify if the user has enough CHZ balance to proceed with the sending
	 * Scenario A: Sending CHZ; user's CHZ balance should be enough to cover sending amount plus the network fee.
	 * Scenario B: Sending Token; user's CHZ balance should be enough to cover the network fee.
	 */
	const isFundInsufficient = useMemo(() => {
		if (!gasFeeEstimate || !amount) {
			return false;
		}

		if (asset?.assetType === 'chiliz') {
			return Number(amount) + gasFeeEstimate > (ncwBalance.chilizBalance?.amount ?? 0);
		} else if (asset?.assetType === 'token') {
			return gasFeeEstimate > (ncwBalance.chilizBalance?.amount ?? 0);
		}

		return false;
	}, [amount, asset, gasFeeEstimate, ncwBalance]);

	/**
	 * Whether this transfering is enabled or not
	 * When the user has enough fund and Biconomy doesn't block this transaction, the transfering is good to go.
	 */
	const isTransferEnabled = useMemo(() => !isFundInsufficient && !isPending, [isFundInsufficient, isPending]);

	/**
	 * If we are missing any of the required data, we should show a loading state.
	 *
	 * TODO: Simply return isLoading as the only key in the object.
	 */
	if (!recipient || !fromAddress || !nCWallet || !amount || !asset || !gasFeeEstimate) {
		return {
			addressCardData: { isLoading: true },
			amount,
			asset,
			handleClickConfirm: undefined,
			intlTexts,
			isFundInsufficient,
			isTransferEnabled,
			priceInfoCardData: undefined,
			toAddress: undefined,
			transferAmountCardData: undefined,
			transferFeeCardData: undefined
		};
	}

	const addressCardData = {
		fromAddress,
		recipient
	};

	/**
	 * Helps to format the gas fee for the user to see.
	 */
	const formattedFeeAmount = formatCrypto(gasFeeEstimate, 'CHZ');

	/**
	 * Helps to format the fiat equivalent of the gas fee for the user to see.
	 */
	const formattedConvertedFee = convertChilizToFiatAndFormat(gasFeeEstimate) ?? '???';

	/**
	 * Transfer Fee Details
	 */
	const transferFeeCardData = {
		formattedConvertedFee,
		formattedFee: formattedFeeAmount,
		recipient
	} satisfies NCWConfirmTransferFeeCardProps;

	/**
	 * Helps to format the amount and fee for the user to see.
	 */
	const formattedAmount = formatCrypto(amount, asset.shortCode ?? '');

	/**
	 * Transfer Amount Details
	 */
	const transferAmountCardData = {
		assetImageUri: asset.imageUri ?? SsuLogoPlaceholder,
		assetName: asset.assetName,
		formattedAmount,
		isChiliz: asset.shortCode === 'CHZ'
	} satisfies NCWConfirmTransferAmountCardProps;

	const redirectToHome = () => replace('/nc-wallet');

	/**
	 * callback to deliver the success drawer
	 */
	const openSuccessDrawer = (transactionHash: string) =>
		ModalService.addModal({
			isClosable: false,
			renderComponent: ({ close }) => (
				<NCWSendResumeDrawer
					closeModal={() => {
						close();
						redirectToHome();
					}}
					onTransactionHistoryRedirect={() => {
						handleTransactionNavigation(transactionHash);
						if (!isTransactionHistoryEnabled) redirectToHome();
						close();
					}}
				/>
			)
		});

	/**
	 * callback to deliver the error drawer
	 */
	const openErrorDrawer = () =>
		ModalService.addModal({
			isClosable: false,
			renderComponent: ({ close }) => (
				<NCWSendErrorDrawer
					onPrimaryButtonActivate={() => {
						close();
						replace('/nc-wallet/send');
					}}
					onSecondaryButtonActivate={() => {
						close();
						setAsset(undefined);
						setAmount(undefined);
						replace('/support');
					}}
				/>
			)
		});

	/**
	 *	Handles the user's confirmation of the transaction.
	 *
	 * @returns {Promise<void>} - A promise that resolves when the user's transaction finishes, be it successful or not.
	 */
	const handleClickConfirm = async () => {
		if (!transaction) {
			replace('/nc-wallet');
			openErrorDrawer();

			return;
		}

		await sendTransaction({
			onError: () => {
				replace('/nc-wallet');
				openErrorDrawer();
			},
			onSuccess: ok => {
				openSuccessDrawer(ok.transactionHash);
			},
			transaction
		});
	};

	return {
		addressCardData,
		amount,
		handleClickConfirm,
		intlTexts,
		isFundInsufficient,
		isTransferEnabled,
		isTransferPending: isPending,
		toAddress,
		transferAmountCardData,
		transferFeeCardData
	};
};
